import { motion } from "framer-motion";
import styles from "./button.module.scss";

interface ButtonProps {
  disabled?: boolean;
  variant?:
    | "primary"
    | "secondary"
    | "info"
    | "error"
    | "fullWidthTransparentPrimary"
    | "fullWidth";
  backgroundColor?: string;
  size?: "small" | "medium" | "large";
  className?: string;
  label?: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  type?: "button" | "submit" | "reset";
}

const Button = ({
  disabled = false,
  variant = "primary",
  size = "medium",
  backgroundColor,
  label,
  icon,
  isLoading = false,
  className = "",
  children,
  type = "button",
  ...props
}: ButtonProps) => {
  const isFullWidth = variant === "fullWidth";
  const variantClass =
    styles[
      variant === "fullWidthTransparentPrimary"
        ? "storybookButtonFullWidthTransparentPrimary"
        : `storybookButton${variant.charAt(0).toUpperCase() + variant.slice(1)}`
    ];
  const sizeClass =
    styles[`storybookButton${size.charAt(0).toUpperCase() + size.slice(1)}`];
  const disabledClass = disabled ? styles.storybookButtonDisabled : "";

  return (
    <motion.button
      type={type}
      className={`${styles.storybookButton} ${sizeClass} ${variantClass} ${disabledClass} ${className}`}
      style={{
        backgroundColor,
        width: isFullWidth ? "100%" : "",
        color: isFullWidth ? "white" : "",
      }}
      {...props}
      disabled={disabled || isLoading}
      data-testid="button"
      whileHover={disabled ? undefined : { scale: 1.05 }}
      whileTap={disabled ? undefined : { scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
    >
      {isLoading ? (
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className={styles.spinner}
        >
          🔄
        </motion.span>
      ) : (
        <>
          {icon && <span className={styles.iconWrapper}>{icon}</span>}
          {children ? children : label && <span>{label}</span>}
        </>
      )}
    </motion.button>
  );
};

export default Button;
